import { createContext, useState, useEffect, ReactNode } from 'react';
import { datadogLogs } from '@datadog/browser-logs';
import { useNavigate } from 'react-router-dom';

interface ProgressData {
  processId: string;
  progress: number;
  resultUrl: string;
  // 'unknown': the BFF found only an expired status record, so the order's outcome cannot be told.
  status: 'processing' | 'completed' | 'failed' | 'unknown';
  statusUrl: string;
  errorMessage: string;
}

interface ProgressContextType {
  sseUrl: string | null;
  setSseUrl: (value: string | null) => void;
  data: ProgressData | null;
  projectName: string;
  setProjectName: (value: string) => void;
  isProgressPopupOpen: boolean;
  setIsProgressPopupOpen: (value: boolean) => void;
}

export const ProgressContext = createContext<ProgressContextType>({
  sseUrl: null,
  setSseUrl: (value) => value,
  data: null,
  projectName: '',
  setProjectName: (value) => value,
  isProgressPopupOpen: false,
  setIsProgressPopupOpen: (value) => value,
});

export const ProgressProvider = ({ children }: { children: ReactNode }) => {
  const navigate = useNavigate();

  const [sseUrl, setSseUrl] = useState<string | null>(() => {
    return localStorage.getItem('sseUrl');
  });
  const [data, setData] = useState<ProgressData | null>(null); // Holds the SSE data
  const [isProgressPopupOpen, setIsProgressPopupOpen] = useState(false);
  const [projectName, setProjectName] = useState('');
  const [retry, setRetry] = useState(0); // Tracks connection retries

  useEffect(() => {
    if (sseUrl) {
      setIsProgressPopupOpen(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!sseUrl) {
      setIsProgressPopupOpen(false);
      setProjectName('');
      setData(null);
      setRetry(0);
      localStorage.removeItem('sseUrl');
      return;
    }

    localStorage.setItem('sseUrl', sseUrl);

    let eventSource;

    const connect = () => {
      eventSource = new EventSource(sseUrl);

      // Handle incoming messages
      eventSource.onmessage = (event) => {
        const parsedData: ProgressData = JSON.parse(event.data);
        if (
          parsedData.status === 'completed' ||
          parsedData.status === 'failed' ||
          parsedData.status === 'unknown'
        ) {
          if (parsedData.resultUrl) {
            parsedData.resultUrl = parsedData.resultUrl.replace('/result', '/survey-result');
          }

          eventSource.close(); // Close the connection on completion
        }
        // Fix url from response
        setData(parsedData);
        setRetry(0); // Reset retry count on successful message
      };

      // Handle errors
      eventSource.onerror = () => {
        datadogLogs.logger.error('Progress SSE: Connection error. Retrying...', {
          name: 'useProgress',
          retryCount: retry + 1,
          sseUrl,
        });

        // Close the connection and retry
        eventSource.close();
        const att = retry + 1;
        setRetry(att);
        if (att < 3) {
          // Retry connection after 3 seconds
          setTimeout(connect, 3000);
        } else {
          setSseUrl(null);
          datadogLogs.logger.error('Progress SSE: Connection error. Please try again later.', {
            name: 'useProgress',
            maxRetries: 3,
            sseUrl,
          });
        }
      };
    };

    // Starting the connection
    connect();

    return () => {
      // Closing SSE connection on component unmount
      eventSource?.close();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sseUrl]);

  useEffect(() => {
    if (data?.status === 'failed') {
      // Keep the data and the popup so the customer actually sees the failure; clearing the stream
      // URL here used to close both at once. Only forget the stream, so a reload does not reopen it.
      localStorage.removeItem('sseUrl');
    }

    if (data?.status === 'unknown') {
      // Nothing useful to show: close quietly and stop blocking new orders.
      setSseUrl(null);
    }

    if (data?.status === 'completed' && data?.resultUrl && isProgressPopupOpen) {
      // Redirect after 3 seconds to survey result page if progress popup is open
      const redirectTimeout = setTimeout(() => {
        if (isProgressPopupOpen) {
          navigate(data?.resultUrl);
          setIsProgressPopupOpen(false);
        }
      }, 3000);

      // Cleanup the timeout if the component unmounts or status changes
      return () => clearTimeout(redirectTimeout);
    }
  }, [data, isProgressPopupOpen, navigate, setIsProgressPopupOpen]);

  return (
    <ProgressContext.Provider
      value={{
        sseUrl,
        setSseUrl,
        data,
        projectName,
        setProjectName,
        isProgressPopupOpen,
        setIsProgressPopupOpen,
      }}
    >
      {children}
    </ProgressContext.Provider>
  );
};
